<template>
  <div>
    <!-- Product Details -->
    <form @submit.prevent="saveProductDetails" class="flex flex-wrap py-4">
      <div class="w-full py-2">
        <label class="uppercase font-semibold text-sm">Product Code</label>
        <input
            v-model="localProductDetails.product_code"
            class="w-full bge-input bge-input-spacing rounded"
            type="text"
            required
            placeholder="Product Code..."
        />
      </div>

      <div class="w-full py-2">
        <label class="uppercase font-semibold text-sm">Name</label>
        <input
            v-model="localProductDetails.name"
            class="w-full bge-input bge-input-spacing rounded"
            type="text"
            required
            placeholder="Product Name..."
        />
      </div>

      <div class="w-full py-2">
        <label class="uppercase font-semibold text-sm">Description</label>
        <textarea
            v-model="localProductDetails.description"
            placeholder="Product Description..."
            class="w-full bge-input bge-input-spacing rounded"
            rows="10"
        ></textarea>
      </div>

      <div class="flex w-full">
        <div class="w-1/2 pr-2 mb-4">
          <label class="block uppercase font-semibold text-sm mb-1">Set Verified Date</label>
          <Calendar v-model="localProductDetails.verified_date" dateFormat="dd/mm/yy" :showIcon="true" />
        </div>

        <div class="w-1/2 pl-2 mb-4">
          <label class="block uppercase font-semibold text-sm mb-1">Set Verification Period</label>
          <select v-model="localProductDetails.verification_period" class="w-full bge-input bge-input-spacing rounded">
            <option v-for="time in verificationPeriods" :key="time.data_master_id" :value="time.value">
              {{ time.name }}
            </option>
          </select>
        </div>
      </div>

      <div class="w-full">
        <button
            type="button"
            @click="saveProductDetails"
            class="float-right mt-5 flex items-center bg-green-200 hover:bg-green-300 text-secondary py-1 px-3 rounded border border-green-400 hover:border-green-500 hover:shadow active:shadow-inner transition duration-100"
        >
          <svg class="stroke-current h-5 w-5 mr-2" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24">
            <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
            <path d="M17 21v-8H7v8M7 3v5h8" />
          </svg>
          <span>Update Product Details</span>
        </button>
      </div>
    </form>

    <!-- Clean Verify -->
    <div class="w-full mt-8 pt-6 border-t border-gray-200">
      <div class="w-full bg-gray-50 border border-gray-200 rounded p-5">
        <!-- Header -->
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center">
            <p class="text-secondary font-semibold">Clean Verify</p>

            <!-- Sharper Help Icon -->
            <button
                type="button"
                class="ml-2 inline-flex items-center justify-center w-8 h-8 rounded-full bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-100 hover:shadow-sm transition"
                title="What is Clean Verify?"
                @click="toggleHelp"
            >
              <!-- crisp info icon -->
              <svg xmlns="http://www.w3.org/2000/svg" width="24px" height="24px" viewBox="0 0 24 24" fill="currentColor" class="text-gray-600 hover:text-gray-800 transition">
                <path fill="none" d="M0 0h24v24H0z"></path>
                <path d="M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z"></path>
              </svg>
            </button>
          </div>

          <svg class="t7" xmlns="http://www.w3.org/2000/svg" width="24px" height="24px" viewBox="0 0 24 24" fill="#000000" focusable="false">
           </svg>
          <p class="text-xs text-gray-500">Click items to toggle, then save.</p>
        </div>

        <!-- Help text (toggle only) -->
        <div v-if="showHelp" class="mb-4">
          <div class="bg-white border border-gray-200 rounded p-4">
            <p class="text-sm text-gray-700 leading-relaxed">
              This section tracks the <span class="font-semibold">completion status</span> of each product area.
              Once you finish a tab (Pricing, Stock, Images, etc.), tick it here and save so others can see progress
              and continue work based on what is already completed.
            </p>
          </div>
        </div>

        <!-- Tiles -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div
              v-for="opt in cleanVerifyOptions"
              :key="opt.key"
              role="checkbox"
              :aria-checked="localCleanVerify[opt.key] === true ? 'true' : 'false'"
              tabindex="0"
              @click="toggleCleanVerify(opt.key)"
              @keydown.enter.prevent="toggleCleanVerify(opt.key)"
              @keydown.space.prevent="toggleCleanVerify(opt.key)"
              :class="cleanTileClass(localCleanVerify[opt.key] === true)"
          >
            <!-- status icon -->
            <span class="mr-3">
              <svg
                  v-if="localCleanVerify[opt.key] === true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="text-green-600"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M8 12l2.5 2.5L16.5 9"></path>
              </svg>

              <svg
                  v-else
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="text-gray-400"
              >
                <circle cx="12" cy="12" r="10"></circle>
              </svg>
            </span>

            <span class="text-sm font-semibold" :class="localCleanVerify[opt.key] ? 'text-green-800' : 'text-secondary'">
              {{ opt.label }}
            </span>
          </div>
        </div>

        <!-- Comment -->
        <div class="w-full mt-4">
          <label class="uppercase font-semibold text-sm">Clean Verify Comment</label>
          <textarea
              v-model="localCleanVerifyComment"
              placeholder="Add notes or progress update..."
              class="w-full bge-input bge-input-spacing rounded mt-2 notes-textarea"
              rows="4"
          ></textarea>
        </div>

        <!-- Save -->
        <div class="w-full">
          <button
              type="button"
              @click="saveCleanVerify"
              class="float-right mt-5 flex items-center bg-blue-200 hover:bg-blue-300 text-secondary py-1 px-3 rounded border border-blue-400 hover:border-blue-500 hover:shadow active:shadow-inner transition duration-100"
          >
            <svg class="stroke-current h-5 w-5 mr-2" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24">
              <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
              <path d="M17 21v-8H7v8M7 3v5h8" />
            </svg>
            <span>Save Clean Verify</span>
          </button>
        </div>

        <div class="clear-both"></div>
      </div>
    </div>
  </div>
</template>

<script>
import Calendar from "primevue/calendar";
import notificationMixin from "@/mixins/notificationMixin";

function buildDefaultCleanVerify() {
  return {
    pricing: false,
    product_details: false,
    stock_details: false,
    supplier_details: false,
    bill_of_materials: false,
    linked_products: false,
    images: false,
    faqs: false,
    service_templates: false,
    certificates: false,
    documents: false,
  };
}

function hydrateCleanVerify(src) {
  const base = buildDefaultCleanVerify();
  if (!src) return base;

  const keys = Object.keys(base);
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    if (Object.prototype.hasOwnProperty.call(src, k)) {
      base[k] = src[k] === true;
    }
  }
  return base;
}

export default {
  mixins: [notificationMixin],
  components: { Calendar },
  props: {
    product: {
      required: true,
      type: Object,
    },
  },

  data() {
    return {
      localProductDetails: {},
      verificationPeriods: [],
      localCleanVerify: buildDefaultCleanVerify(),
      localCleanVerifyComment: "",

      showHelp: false,
    };
  },

  watch: {
    product: {
      immediate: true,
      deep: true,
      handler(newVal) {
        this.applyProductToLocal(newVal);
      },
    },
  },

  computed: {
    cleanVerifyOptions() {
      return [
        { key: "pricing", label: "Pricing" },
        { key: "product_details", label: "Product Details" },
        { key: "stock_details", label: "Stock Details" },
        { key: "supplier_details", label: "Supplier Details" },
        { key: "bill_of_materials", label: "Bill of Materials" },
        { key: "linked_products", label: "Linked Products" },
        { key: "images", label: "Images" },
        { key: "faqs", label: "FAQs" },
        { key: "service_templates", label: "Service Templates" },
        { key: "certificates", label: "Certificates" },
        { key: "documents", label: "Documents" },
      ];
    },
  },

  async mounted() {
    await this.fetchVerificationPeriods();
  },

  methods: {
    toggleHelp() {
      this.showHelp = !this.showHelp;
    },

    applyProductToLocal(p) {
      if (!p) return;

      this.localProductDetails = {
        ...p,
        verified_date: p.verified_at ? this.formatTimestamp(p.verified_at) : null,
      };

      this.localCleanVerify = hydrateCleanVerify(p.clean_verify);
      this.localCleanVerifyComment = p.clean_verify_comment ? p.clean_verify_comment : "";
    },

    cleanTileClass(selected) {
      return [
        "flex items-center rounded px-3 py-2 cursor-pointer select-none transition",
        "border",
        selected
            ? "bg-green-50 border-green-400 shadow-sm"
            : "bg-white border-gray-200 hover:bg-gray-50",
        "focus:outline-none focus:ring-2 focus:ring-green-200",
      ].join(" ");
    },

    toggleCleanVerify(key) {
      this.localCleanVerify[key] = this.localCleanVerify[key] === true ? false : true;
    },

    async saveProductDetails() {
      try {
        if (this.localProductDetails.verified_date) {
          this.localProductDetails.verified_at = this.$moment(
              this.localProductDetails.verified_date,
              "DD-MM-YYYY"
          ).unix();
        } else {
          this.localProductDetails.verified_at = null;
        }

        await this.ProductVerifiedService.updateProduct(
            this.localProductDetails.product_id,
            this.localProductDetails
        );

        const fresh = await this.ProductVerifiedService.getProductById(
            this.localProductDetails.product_id
        );
        this.applyProductToLocal(fresh);

        const updatedCode = (fresh.product_code || "").toString().toLowerCase();

        this.$emit("complete", { productCode: updatedCode });

        this.notifySuccess("Product details updated successfully.");
      } catch (error) {
        console.error("Error updating product details:", error);
        const msg =
            error && error.data && error.data.message
                ? error.data.message
                : "Failed to update product details.";
        this.notifyError(msg);
      }
    },


    async saveCleanVerify() {
      try {
        const payload = {
          clean_verify: { ...this.localCleanVerify },
          clean_verify_comment: this.localCleanVerifyComment,
        };

        await this.ProductVerifiedService.updateCleanVerify(this.localProductDetails.product_id, payload);

        const fresh = await this.ProductVerifiedService.getProductById(this.localProductDetails.product_id);
        this.applyProductToLocal(fresh);

        this.$emit("complete");
        this.notifySuccess("Clean verify saved successfully.");
      } catch (error) {
        console.error("Error saving clean verify:", error);
        const msg = error && error.data && error.data.message ? error.data.message : "Failed to save clean verify.";
        this.notifyError(msg);
      }
    },

    formatTimestamp(unixTimestamp) {
      return this.$moment.unix(unixTimestamp).format("DD-MM-YYYY");
    },

    async fetchVerificationPeriods() {
      try {
        const response = await this.DatamasterService.getDataMasters(
            "?type=VerifiedPeriod&is_active=true&noPaging=true&orderBy=order&order=1"
        );
        this.verificationPeriods = this.mapDropdownOptions(response);
      } catch (error) {
        console.error("Error fetching verification periods:", error);
      }
    },

    mapDropdownOptions(response) {
      return response.map((item) => ({
        data_master_id: item.data_master_id,
        name: item.name,
        value: item.value || "",
      }));
    },
  },
};
</script>

<style scoped>
/* Scrollable comment box (no ruled lines) */
.notes-textarea {
  max-height: 140px;
  overflow-y: auto;
  resize: vertical;
}
</style>
